import React from 'react';
import { logError, ERROR_SEVERITY } from '../utils/errorHandler';
import errorStyles from '../styles/Error.module.css';
import { isDevelopment } from '../env/config';

/**
 * Error Boundary - Catches JavaScript errors in child component tree
 * Logs the error and displays a fallback UI
 *
 * Usage:
 * <ErrorBoundary>
 *   <YourComponent />
 * </ErrorBoundary>
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log the error with full context
    logError(
      error,
      {
        component: 'ErrorBoundary',
        componentStack: errorInfo.componentStack,
        action: 'componentDidCatch',
      },
      ERROR_SEVERITY.CRITICAL
    );

    // Update state with error details
    this.setState({
      error,
      errorInfo,
    });

    // In production, you might want to send to error tracking service
    // if (!isDevelopment()) {
    //   // Send to Sentry, LogRocket, etc.
    // }
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });

    // Optionally reload the page
    // window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      return (
        <div className={errorStyles.container}>
          <div className={errorStyles.content}>
            <div className={errorStyles.iconContainer}>
              <span className={errorStyles.icon}>⚠️</span>
            </div>

            <h2 className={errorStyles.title}>Oops! Something went wrong</h2>

            <p className={errorStyles.message}>
              We encountered an unexpected error. Our team has been notified and
              we're working on it.
            </p>

            <div className={errorStyles.actions}>
              <button onClick={this.handleReset} className={errorStyles.button}>
                Try Again
              </button>

              <button
                onClick={() => window.location.reload()}
                className={errorStyles.secondaryButton}
              >
                Reload Page
              </button>
            </div>

            {/* Show error details in development */}
            {isDevelopment() && this.state.error && (
              <details className={errorStyles.details}>
                <summary className={errorStyles.detailsSummary}>
                  Error Details (Dev Only)
                </summary>
                <div className={errorStyles.errorInfo}>
                  <p>
                    <strong>Error:</strong> {this.state.error.toString()}
                  </p>
                  <pre className={errorStyles.errorStack}>
                    {this.state.errorInfo?.componentStack}
                  </pre>
                </div>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
